<template>
    <div v-if="isVisible" class="user-invoices-box box">
        <div class="box-title">
            Invoices
            <div
                v-if="upcomingInvoice && !isLifetimeSubscriptionSelected"
                class="next-invoice"
            >
                Your next bill for <span class="detail">{{ formatPrice( upcomingInvoice.amount_due ) }}
                </span> is coming on <span class="detail">{{ date( upcomingInvoice.next_payment_attempt ) }}</span>
            </div>
        </div>
        <div class="box-content">
            <transition
            name="fade"
            mode="out-in"
            >
                <div
                    v-if="isLoading"
                    class="loader"
                >
                    <i class="fas fa-spinner fa-spin loader" />
                </div>
                <template v-else>
                    <ul class="invoices-listing">
                        <template v-for="(invoice, index) in invoices">
                            <li v-if=" freePlan !== invoice.lines.data[0].plan.id" :key="index">
                                <div class="invoices-header">
                                    <div class="invoices-date">
                                        Date
                                    </div>
                                    <div class="invoices-amount">
                                        Amount
                                    </div>
                                    <div class="invoices-number">
                                        # of Sites
                                    </div>
                                    <div class="invoices-plan">
                                        Plan
                                    </div>
                                </div>
                                <div class="invoices-details">
                                    <div class="invoices-date">
                                        {{ dateExplicit( invoice.created ) }}
                                    </div>
                                    <div class="invoices-amount">
                                        {{ formatPrice( invoice.total ) }}
                                    </div>
                                    <div class="invoices-number">
                                        {{ invoiceSites( invoice ) }}
                                    </div>
                                    <div class="invoices-plan">
                                        {{ invoicePlan( invoice ) }}
                                    </div>
                                    <div class="invoices-download">
                                        <a :href="`/api/billing/invoices/${invoice.id}/download`">
                                            <i class="fas fa-file-pdf" />
                                            <span>View invoice</span>
                                        </a>
                                    </div>
                                </div>
                            </li>
                        </template>
                    </ul>
                </template>
        </transition>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import usdFormatter from '@/utils/usdFormatter';
import moment from 'moment';
import toasts from '@/mixins/toasts';

export default {
    mixins: [toasts],

    data: () => ({
        freePlan: 'plan_H4cQhLOO9JbveX',
    }),

    computed: {
        ...mapState({
            upcomingInvoice: state =>
                state.currentSubscription?.name && 'lifetime' !== state.currentSubscription.name
                    ? state.upcomingInvoice : null,
            invoices: state =>
                state.currentSubscription?.stripe_id && 'none' !== state.currentSubscription.name
                    ? state.invoices : null,
            isVisible: state => 'none' !== state.currentSubscription.stripe_id,
            isLoading: state => null === state.invoices,
            currentSubscription: state => state.currentSubscription,
        }),

        ...mapGetters(['isLifetimeSubscriptionSelected']),
    },

    watch: {
        currentSubscription: {
            immediate: true,

            /**
             * Watch changes on subscription
             *
             * @param {object} newSubscription New loaded subscription.
             * @param {object} oldSubscription Old loaded subscription.
             */
            handler(newSubscription, oldSubscription) {
                if ((oldSubscription?.stripe_id &&
                    (newSubscription.stripe_id !== oldSubscription?.stripe_id)) ||
                    this.isLifetimeSubscriptionSelected
                ) {
                    this.loadUpcomingInvoice();
                    this.loadInvoices({ force: true });
                }
            },
        },
    },

    /**
     * Updated hook
     */
    updated() {
        if (false === this.invoices) {
            this.toastError({
                message: {
                    title: 'We could not import your invoices. Please try again later',
                },
            });
        }
    },

    methods: {
        ...mapActions([
            'loadInvoices',
            'loadUpcomingInvoice',
        ]),

        /**
         * Formats date
         *
         * @param   { number }  date  Date timestamp
         * @returns  { string }  Formated date.
         */
        date(date) {
            const momentDate = moment.unix(date);

            return momentDate.isSame(new Date(), 'day') || null === date
                ? 'today' : momentDate.format('MMM D, YYYY');
        },

        /**
         * Formats explicit date
         *
         * @param   { number }  date  Date timestamp
         * @returns  { string }  Formated date.
         */
        dateExplicit(date) {
            return moment.unix(date).format('MMM D, YYYY');
        },

        /**
         * Formats price in USD
         *
         * @param { number } price Price in cents
         * @returns {string} Formated price.
         */
        formatPrice(price) {
            return usdFormatter(price);
        },

        /**
         * Return invoice plan
         *
         * @param  { object } invoice Invoice object.
         * @returns { string } invoice plan.
         */
        invoicePlan(invoice) {
            const currentInvoice = [...invoice.lines.data].pop();
            const nickname = currentInvoice.plan.nickname;

            return `Fusebox Pro ${nickname}`;
        },

        /**
         * Return invoice sites
         *
         * @param  { object } invoice Invoice object.
         * @returns { string } invoice sites.
         */
        invoiceSites(invoice) {
            const currentInvoice = [...invoice.lines.data].pop();

            return currentInvoice.quantity;
        },
    },
};
</script>
